import { cn } from "@src/utils"
import { withUniwind } from "uniwind"
import _MCIcon from "@react-native-vector-icons/material-design-icons"
import { Image as ExpoImage } from "expo-image"
import {
  Text as _Text,
  Platform,
  TextProps,
  TextInput as _TextInput,
  TextInputProps,
  Pressable,
  PressableProps,
  ActivityIndicator as _ActivityIndicator,
  Modal,
  View,
  ActivityIndicatorProps,
  StyleSheet,
} from "react-native"
import A, { useAnimatedStyle, withSpring } from "react-native-reanimated"
import { useReanimatedKeyboardAnimation } from "react-native-keyboard-controller"

type Props = TextProps & {
  forceRight?: boolean
}

export function Text({ className, forceRight, ...props }: Props) {
  const hasAlignment = className?.includes("text-left") || className?.includes("text-right") || className?.includes("text-center")
  let ctx = cn(`text-base ${hasAlignment ? "" : "text-left "}`, className)

  if (Platform.OS === "web") {
    ctx = ctx.replace("text-left", "text-right")
  }

  return <_Text {...props} selectable={props.selectable ?? true} className={ctx} />
}

export function TextInput({ className, multiline, ...props }: TextInputProps & { ref?: React.Ref<_TextInput> }) {
  return (
    <_TextInput
      className={cn("h-11 px-4 rounded bg-white text-gray-900 text-right border border-gray-300 focus:border-blue-500 ", multiline && "pt-3 h-[100] ", className)}
      autoComplete="off"
      autoCapitalize="none"
      placeholderTextColor="#9CA3AF"
      autoCorrect={false}
      underlineColorAndroid="transparent"
      {...(multiline ? { textAlignVertical: "top", multiline: true, scrollEnabled: true } : { scrollEnabled: false })}
      {...props}
    />
  )
}

export function Button({ className, loading, disabled, children, ...props }: { children: React.ReactNode; loading?: boolean } & PressableProps) {
  const DISABLED = disabled || loading

  return (
    <Pressable
      className={cn("bg-blue-600 py-2 md:py-3 px-3 md:px-4 rounded items-center ", DISABLED ? "opacity-50 pointer-events-none" : "hover:opacity-90", className)}
      disabled={DISABLED}
      {...props}
    >
      <Text className={cn("text-white font-semibold text-md", loading && "opacity-0")}>{children}</Text>
      {loading ? <ActivityIndicator className="absolute top-0 left-0 right-0 bottom-0" color={disabled ? "black" : "white"} /> : null}
    </Pressable>
  )
}

export function ModalWrapper({ children, onClose, className }: { children: React.ReactNode; onClose: () => void; className?: string }) {
  const { height, progress } = useReanimatedKeyboardAnimation()

  const containerStyle = useAnimatedStyle(() => {
    return {
      transform: [{ translateY: withSpring(height.value * progress.value * 0.3) }],
    }
  }, [])

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onClose}>
      <Pressable className="bg-black/60" style={StyleSheet.absoluteFill} onPress={onClose} />
      <A.View style={containerStyle} pointerEvents="box-none" className="mt-auto mb-auto px-2">
        <View onStartShouldSetResponder={() => true} className={cn("p-2 border mx-auto bg-white w-full max-w-xl rounded overflow-hidden shadow-2xl", className)}>
          {children}
        </View>
      </A.View>
    </Modal>
  )
}

export function ActivityIndicator({ className, ...props }: ActivityIndicatorProps) {
  return <_ActivityIndicator className={cn("", className)} {...props} />
}

export function Checkbox({ value, onChange, label, className }: { value?: boolean; onChange: (v: boolean) => void; label: string; className?: string }) {
  return (
    <Pressable onPress={() => onChange(!value)} className={cn("flex-row items-center justify-between py-3 border-b border-gray-100 active:opacity-70", className)}>
      <Text className="text-base">{label}</Text>
      <View className={`w-6 h-6 rounded border-2 items-center justify-center ${value ? "bg-blue-500 border-blue-500" : "bg-white border-gray-300"}`}>
        {value && <MCIcon name="check" size={16} color="white" />}
      </View>
    </Pressable>
  )
}

export const Image = withUniwind(ExpoImage)
export const MCIcon = withUniwind(_MCIcon)
